import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField, CheckboxField } from "@mimber/forms"
import { Button, message, Typography } from "@mimber/ui-web"
import {
  mfaChallengeSchema,
  confirmSignIn,
  mapCognitoError,
  rememberDevice,
} from "@mimber/auth"
import { fetchAuthSession } from "aws-amplify/auth"
import type {
  MFAChallengeFormProps,
  MFAChallengeFormData,
} from "./MFAChallengeForm.types"

const { Paragraph } = Typography

const METHOD_LABELS: Record<MFAChallengeFormProps["method"], string> = {
  totp: "Enter the code from your authenticator app",
  sms: "Enter the code sent to your phone",
  email: "Enter the code sent to your email",
}

export const MFAChallengeForm: React.FunctionComponent<
  MFAChallengeFormProps
> = ({ method, onSuccess, onCancel, onUseRecoveryCode }) => {
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<MFAChallengeFormData>({
    resolver: zodResolver(mfaChallengeSchema),
    defaultValues: {
      code: "",
      trustDevice: false,
    },
  })

  const handleSubmit = async (data: MFAChallengeFormData) => {
    setIsLoading(true)
    try {
      const result = await confirmSignIn(data.code)
      if (result.isSignedIn) {
        await fetchAuthSession({ forceRefresh: true })
        if (data.trustDevice) {
          try {
            await rememberDevice()
          } catch {
            // Best-effort — don't block login on failure
          }
        }
        onSuccess()
      }
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="mb-6 text-center">
        <Paragraph>{METHOD_LABELS[method]}</Paragraph>
      </div>

      <Form form={form} onSubmit={handleSubmit}>
        <InputField
          name="code"
          type="text"
          label="Verification Code"
          placeholder="Enter 6-digit code"
          maxLength={6}
          required
        />
        <CheckboxField name="trustDevice">Trust this device</CheckboxField>
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          className="w-full mb-3"
          data-testid="mfa-submit-button"
        >
          Verify
        </Button>
      </Form>

      <div className="flex justify-between items-center">
        <Button type="link" onClick={onCancel} data-testid="mfa-cancel-button">
          Cancel
        </Button>
        {onUseRecoveryCode && (
          <Button
            type="link"
            onClick={onUseRecoveryCode}
            data-testid="use-recovery-code-link"
          >
            Use recovery code
          </Button>
        )}
      </div>
    </>
  )
}
